import { motion } from "framer-motion";
import { MapPinOff, EyeOff, Fingerprint, ShieldOff, ScanLine, KeyRound, Lock, Shield, Ghost, Radar, Wifi, Bug, BookOpen, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { ASSETS } from "../data";
import { useContent } from "../context/ContentContext";

const ICON_MAP = { MapPinOff, EyeOff, Fingerprint, ShieldOff, ScanLine, KeyRound, Lock, Shield, Ghost, Radar, Wifi, Bug };

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

export const WhatsInside = () => {
  const { content } = useContent();
  const wi = content.whatsInside || {};
  const features = wi.features || [];
  return (
    <section id="pack" className="relative py-24 lg:py-36 max-w-[1400px] mx-auto px-6 lg:px-10">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-accent">// What's Inside</span>
          <h2 className="mt-5 font-display font-black uppercase tracking-tighter text-4xl sm:text-6xl leading-[0.9]">
            {wi.heading}
          </h2>
        </div>
        <p className="max-w-md font-body text-white/50 leading-relaxed">
          {wi.sub}
        </p>
      </div>

      <div className="mb-10">
        <Link
          to="/preview"
          data-testid="look-inside-link"
          className="group inline-flex items-center gap-3 border border-accent/50 bg-[#0c0c0c] px-6 py-4 hover:border-accent hover:bg-[#140606] transition-colors"
        >
          <BookOpen size={18} className="text-accent" />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/80">Look inside, read a free sample chapter</span>
          <ArrowRight size={15} className="text-accent group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {features.map((f, i) => {
          const Icon = ICON_MAP[f.icon] || Shield;
          return (
            <motion.div
              key={f.title}
              data-testid={`feature-${i}`}
              custom={i}
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              className="group bg-[#050505] p-8 lg:p-10 hover:bg-[#0d0d0d] transition-colors duration-500 hover:-translate-y-1 transition-transform"
            >
              <Icon className="text-accent mb-8" size={28} strokeWidth={1.4} />
              <h3 className="font-display font-bold text-xl mb-3 tracking-tight">{f.title}</h3>
              <p className="font-body text-sm text-white/50 leading-relaxed">{f.desc}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Editorial split image band */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-t-0 border-white/10">
        <div className="relative bg-[#050505] min-h-[280px] overflow-hidden group">
          <img src={ASSETS.hoodie} alt="Anonymity" className="absolute inset-0 h-full w-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
          <div className="relative z-10 p-8 lg:p-10 h-full flex flex-col justify-end">
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-2">Anonymity</span>
            <p className="font-display font-bold text-2xl tracking-tight">Silence your digital noise</p>
          </div>
        </div>
        <div className="relative bg-[#050505] min-h-[280px] overflow-hidden group">
          <img src={ASSETS.forest} alt="Off the grid" className="absolute inset-0 h-full w-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
          <div className="relative z-10 p-8 lg:p-10 h-full flex flex-col justify-end">
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-2">Off The Grid</span>
            <p className="font-display font-bold text-2xl tracking-tight">Live without being watched</p>
          </div>
        </div>
      </div>
    </section>
  );
};
